import { memo, useEffect } from 'react'
import { queryClient } from 'v2/configs/rq-setup'
import { useMonitorMutations } from 'v2/pages/conversations/mutations/call'
import { addChunkToQueryCache } from 'v2/pages/conversations/mutations/conversations'
import { checkSecondaryParticipant, isOneOfMonitoringStates } from 'v2/pages/conversations/utils'
import { toChunkModel } from 'v2/pages/conversations/utils/transformers'
import { useProfileQuery } from 'v2/pages/settings/queries'
import { usePhoneStore } from 'v2/state/phone'
import { ConversationStatus, type RMonitoringData } from 'v2/types/api/conversations'
import { MonitorStates, ParticipantState } from 'v2/types/call'
import { ChunkOrigin } from 'v2/types/chunk.external'
import { PubnubEvents } from 'v2/types/pubnub'
import { PrimaryEvents, primaryEventBus } from 'v2/utils/event-bus/primary'
import { pubnubEventBus } from 'v2/utils/event-bus/pubnub'
import { phoneLogger } from 'v2/utils/logger'
import { phoneSDK } from 'v2/utils/phone-sdk'
import { voiceAudioConstraints } from 'v2/utils/phone-sdk/utils/helpers'
import { errorToast } from 'v2/utils/toasts'
import { FloatingCallWidget } from '../../pages/conversations/components/floating-call-widget'
import { useFeatureFlags } from '../hooks/use-feature-flags'
import { useSipEndpoint } from '../hooks/use-sip-endpoint'
import { conversationQueryKeys } from '../utils/query-key-management'

// Ensure app is not re-rendered unnecessarily
// Remove this once that is fixed
// Issue is with feature flag, which is causing a re-render of the whole app
export const PhoneHandler = memo(
  () => {
    const profile = useProfileQuery()
    const { isCallActive, activeCall } = usePhoneStore()
    const { leaveMonitoring } = useMonitorMutations()
    const flags = useFeatureFlags()

    // SER-5347: fall back to on-demand fetch-or-generate when profile creds are empty
    const endpoint = useSipEndpoint(profile.data?.organizations[0].sip_endpoint, profile.isSuccess)
    const username = endpoint?.username
    const password = endpoint?.password

    const audioConstraints = voiceAudioConstraints(flags.channelVoiceEnabled)

    useEffect(() => {
      if (username && password) {
        phoneSDK.initialize({ username, password }, audioConstraints, flags.usePlivoStunServer)
        phoneSDK.connect([])
      }
    }, [username, password, flags.channelVoiceEnabled, flags.usePlivoStunServer])

    useEffect(() => {
      const unbinds = [
        primaryEventBus.on(PrimaryEvents.REDIRECT_INCOMING_CALL, (toUri) => {
          phoneSDK.redirect(toUri)
        }),

        primaryEventBus.on(PrimaryEvents.CALL_TERMINATED, (isPageUnload) => {
          const cb = async () => {
            const { parentConversation } = usePhoneStore.getState().callConversation
            // If the user is monitoring, leave the monitoring session
            // Feature: CALL_MONITORING
            if (
              checkSecondaryParticipant(parentConversation) &&
              isOneOfMonitoringStates(usePhoneStore.getState().monitoringState) &&
              usePhoneStore.getState().isMonitorInitiator // Only if the user is the one who started the monitoring
            ) {
              await leaveMonitoring.mutateAsync({
                conversationId: parentConversation?.conversation_number ?? '',
                state: ConversationStatus.CLOSED,
                agent_state: MonitorStates.MONITOR,
              })
            }
            usePhoneStore.getState().reset()
            setTimeout(() => {
              queryClient.invalidateQueries({ queryKey: conversationQueryKeys().active })
              // Give sufficient time for BE to update the active conversations
            }, 300)
          }
          // If the page is being unloaded, wait for 1 second before resetting the phone state
          if (isPageUnload) setTimeout(cb, 1000)
          else cb()
        }),

        primaryEventBus.on(PrimaryEvents.APP_ACTIVE, () => {
          phoneSDK.connect([])
        }),

        primaryEventBus.on(PrimaryEvents.REJECT_INCOMING_CALL, (callUUID) => {
          phoneSDK.getSdk()?.client.reject(callUUID)
        }),

        pubnubEventBus.on(PubnubEvents.CCNS, (data) => {
          const headers = [
            `X-Index: ${data.ccns_data.index}`,
            `X-Label: ${data.ccns_data.label}`,
            `X-Plivo-Registrar-Info: ${data.ccns_data.registrar}`,
          ]

          phoneLogger.info('Setting CCNS headers', { headers })
          phoneSDK.connect(headers)
        }),

        pubnubEventBus.on(PubnubEvents.OUTBOUND_CALL_INITIALISED, () => {
          queryClient.invalidateQueries({ queryKey: conversationQueryKeys().active })
        }),

        // Subscribe to primary tab changes to handle SDK registration
        primaryEventBus.on(PrimaryEvents.PRIMARY_TAB_STATUS, (isPrimary) => {
          // Only connect if there is no active session (active call) going on
          if (isPrimary) {
            if (!phoneSDK.isCallActive()) phoneSDK.connect([])
            // If there is an active call, wait for it to terminate before connecting
            else
              primaryEventBus.once(PrimaryEvents.CALL_TERMINATED, () =>
                setTimeout(() => phoneSDK.connect([]), 1000),
              )
          }
        }),

        pubnubEventBus.on(PubnubEvents.OUTBOUND_CALL_CUSTOMER_ADDED, () => {
          usePhoneStore.getState().setCallPickedup(true)
        }),

        pubnubEventBus.on(PubnubEvents.OUTBOUND_CALL_FAILED, () => {
          usePhoneStore.getState().reset()
        }),

        // Feature: CALL_MONITORING
        pubnubEventBus.on(PubnubEvents.MONITOR_FAILURE, (data) => {
          errorToast({
            title: 'Unable to start monitoring',
            description: data.error_message,
          })
        }),

        primaryEventBus.on(PrimaryEvents.HANGUP_CALL, () => {
          phoneSDK.hangup()
        }),

        primaryEventBus.on(PrimaryEvents.ON_INCOMING_CALL, (callDetails) => {
          const meta = callDetails.extraHeaders?.meta
          if (meta === 'Monitored Call' && window.cx.tabManager?.isLastUsedTab()) {
            phoneSDK.answerCall()
            usePhoneStore.getState().setMonitoringState(MonitorStates.MONITOR)
          }

          // Feature: CALL_AUTO_ACCEPT
          if (
            callDetails.autoAccept &&
            callDetails.autoAcceptDelay &&
            callDetails.autoAcceptDelay > 0
          ) {
            setTimeout(
              () => {
                if (window.cx.tabManager?.isLastUsedTab()) phoneSDK.answerCall()
              },
              // Convert delay to milliseconds, fallback to 0 if undefined
              (callDetails.autoAcceptDelay ?? 0) * 1000,
            )
          }
        }),

        pubnubEventBus.on(PubnubEvents.SUPERVISOR_COACHING, (data) => {
          usePhoneStore.getState().setMonitoringState(MonitorStates.COACH)
          addChunkToQueryCache(
            toChunkModel(data.chunk_data, ChunkOrigin.apiFetchChunks),
            data.conversation_number,
          )
        }),

        pubnubEventBus.on(PubnubEvents.SUPERVISOR_LEFT, (data) => {
          usePhoneStore.getState().setMonitoringState(null)
          usePhoneStore.getState().removeParticipant('monitoring_participant')
          addChunkToQueryCache(
            toChunkModel(data.chunk_data, ChunkOrigin.apiFetchChunks),
            data.conversation_number,
          )
        }),

        pubnubEventBus.on(PubnubEvents.SUPERVISOR_BARGING, (data) => {
          const monitoringData = data.chunk_data.attributes?.[0] as RMonitoringData
          const identifier =
            usePhoneStore.getState().isSecondaryParticipant &&
            usePhoneStore.getState().isMonitorInitiator
              ? monitoringData.agent_name
              : monitoringData.supervisor_name
          usePhoneStore.getState().addParticipant({
            state: ParticipantState.MERGED,
            identifier,
            member: 'monitoring_participant',
            participantType: 'monitor',
            startAt: new Date().toISOString(),
          })
          usePhoneStore.getState().setMonitoringState(MonitorStates.BARGE)
          addChunkToQueryCache(
            toChunkModel(data.chunk_data, ChunkOrigin.apiFetchChunks),
            data.conversation_number,
          )
        }),

        // Feature: CALL_MONITORING - Transferred conversation
        // When a conversation is transferred, the agent name is updated to the transfered agent name
        pubnubEventBus.on(PubnubEvents.TRANSFERRED_CONVERSATION, (data) => {
          const transferedAgentName = data.secondary_participants_details[0].agent_name
          usePhoneStore.getState().updateParticipant('monitoring_participant', {
            identifier: transferedAgentName,
          })
        }),

        pubnubEventBus.on(PubnubEvents.ASSISTANCE_REQUESTED, () => {
          usePhoneStore.getState().setMonitoringState(MonitorStates.ASSISTANCE_REQUESTED)
        }),

        pubnubEventBus.on(PubnubEvents.SUPERVISOR_MONITORING, (data) => {
          usePhoneStore.getState().setMonitoringState(MonitorStates.MONITOR_VISIBLE)
          addChunkToQueryCache(
            toChunkModel(data.chunk_data, ChunkOrigin.apiFetchChunks),
            data.conversation_number,
          )
        }),
      ]

      return () => {
        unbinds.forEach((unbind) => unbind())
      }
    }, [])

    if (!activeCall) return null
    if (isCallActive && !phoneSDK.isCallActive()) return null
    // Don't show the widget for playground calls
    if (activeCall.contactName === 'Playground') return null

    return <FloatingCallWidget />
  },
  () => true,
)

PhoneHandler.displayName = 'PhoneHandler'
